import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CRMInteraction } from '@/lib/crm/types';
import { toast } from 'sonner';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

/**
 * Hook to fetch interactions for stakeholders
 * Used by CRM Dashboard in Stakeholder Management Page
 */
export function useStakeholderInteractions(stakeholderIds: string[]) {
  return useQuery({
    queryKey: ['stakeholder-interactions', ...stakeholderIds.sort()],
    queryFn: async () => {
      if (stakeholderIds.length === 0) return [];
      
      // Using chunked query with additional ordering
      const data = await chunkedInQuerySimple<any>(
        supabase,
        'crm_interactions' as any,
        'stakeholder_id',
        stakeholderIds,
        '*',
        (query) => query.order('date', { ascending: false }).limit(500)
      );
      
      // Map database columns to interface
      return (data || []).map(item => ({
        ...item,
        stakeholderId: item.stakeholder_id,
        nextFollowUp: item.next_follow_up,
        createdBy: item.created_by
      })) as CRMInteraction[];
    },
    enabled: stakeholderIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 min cache
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to log new interactions
 */
export function useLogInteraction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (interaction: Omit<CRMInteraction, 'id' | 'created_at' | 'updated_at'>) => {
      // Map to database columns
      const dbData = {
        stakeholder_id: interaction.stakeholder_id || interaction.stakeholderId,
        date: interaction.date,
        type: interaction.type,
        subject: interaction.subject,
        outcome: interaction.outcome,
        notes: interaction.notes,
        attendees: interaction.attendees,
        next_follow_up: interaction.next_follow_up || interaction.nextFollowUp,
        status: interaction.status || 'completed', // NEW
        created_by: interaction.created_by || interaction.createdBy
      };
      
      const { data, error } = await supabase
        .from('crm_interactions')
        .insert(dbData)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-interactions'] });
      toast.success('Tương tác đã được ghi lại');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}

/**
 * Hook to update interactions
 */
export function useUpdateInteraction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CRMInteraction> }) => {
      const { error } = await supabase
        .from('crm_interactions')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-interactions'] });
      toast.success('Tương tác đã được cập nhật');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}

/**
 * Hook to delete interactions
 */
export function useDeleteInteraction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_interactions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-interactions'] });
      toast.success('Tương tác đã bị xóa');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}

/**
 * Hook to fetch follow-ups for stakeholders
 */
export function useStakeholderFollowUps(stakeholderIds: string[]) {
  return useQuery({
    queryKey: ['stakeholder-follow-ups', ...stakeholderIds.sort()],
    queryFn: async () => {
      if (stakeholderIds.length === 0) return [];
      
      // Use chunkedInQuerySimple for stakeholder IDs which can exceed 100
      const data = await chunkedInQuerySimple<any>(
        supabase,
        'stakeholder_follow_ups',
        'stakeholder_id',
        stakeholderIds,
        '*',
        (query) => query.order('follow_up_date', { ascending: true })
      );
      
      return data.map(item => ({
        ...item,
        stakeholderId: item.stakeholder_id,
        followUpDate: item.follow_up_date
      }));
    },
    enabled: stakeholderIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create follow-up
 */
export function useCreateFollowUp() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      stakeholderId, 
      date, 
      type = 'Follow-up',
      subject,
      outcome,
      attendees,
      notes,
      status = 'pending'
    }: { 
      stakeholderId: string; 
      date: string;
      type?: string;
      subject: string;
      outcome?: string;
      attendees?: string;
      notes?: string;
      status?: string;
    }) => {
      // Get current user for created_by field
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('stakeholder_follow_ups')
        .insert({
          stakeholder_id: stakeholderId,
          follow_up_date: date,
          type: type,
          subject: subject,
          outcome: outcome || null,
          attendees: attendees || null,
          notes: notes || null,
          status: status,
          created_by: user?.id // Explicitly set created_by for RLS compliance
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Also update stakeholders.next_follow_up for backward compatibility
      await supabase
        .from('stakeholders')
        .update({ next_follow_up: date })
        .eq('id', stakeholderId);
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-detail'] });
      toast.success('Follow-up đã được tạo');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}

/**
 * Hook to update follow-up
 */
export function useUpdateFollowUp() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      date,
      type,
      subject,
      outcome,
      attendees,
      notes, 
      status 
    }: { 
      id: string; 
      date?: string;
      type?: string;
      subject?: string;
      outcome?: string;
      attendees?: string;
      notes?: string;
      status?: string;
    }) => {
      const updateData: any = { updated_at: new Date().toISOString() };
      if (date) updateData.follow_up_date = date;
      if (type) updateData.type = type;
      if (subject !== undefined) updateData.subject = subject;
      if (outcome !== undefined) updateData.outcome = outcome;
      if (attendees !== undefined) updateData.attendees = attendees;
      if (notes !== undefined) updateData.notes = notes;
      if (status) updateData.status = status;
      
      const { error } = await supabase
        .from('stakeholder_follow_ups')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-detail'] });
      toast.success('Follow-up đã được cập nhật');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}

/**
 * Hook to delete follow-up
 */
export function useDeleteFollowUp() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('stakeholder_follow_ups')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-detail'] });
      toast.success('Follow-up đã bị xóa');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}

/**
 * Hook to update stakeholder follow-up date (deprecated - use useCreateFollowUp instead)
 */
export function useUpdateStakeholderFollowUp() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ stakeholderId, date }: { stakeholderId: string; date: string }) => {
      const { error } = await supabase
        .from('stakeholders')
        .update({ next_follow_up: date })
        .eq('id', stakeholderId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-detail'] });
      toast.success('Follow-up đã được cập nhật');
    },
    onError: (error: Error) => {
      toast.error(`Lỗi: ${error.message}`);
    },
  });
}
